import clip from "text-clipper";

/**
 * Checks if a string with html tags has text content
 * Checks if the string contains media content (image, video, iframe, etc)
 */
export const toCapitalizedWord = (word: string): string => {
  if (!word) return word; // if word is empty string, return it
  const inputWord = word.trim();

  return inputWord.charAt(0).toUpperCase() + inputWord.slice(1).toLowerCase();
};

export const isStringWithContent = (stringWithHtmlTags: string): boolean => {
  // get the text content without html tags
  // check if the html content contains a single empty tag (ex: <p></p>)
  const stringWithoutTags = clip(
    stringWithHtmlTags,
    stringWithHtmlTags?.length,
    {
      html: true,
      stripTags: true,
    }
  );

  // check that text content has characters other than spaces
  // check if text content contains any images or videos (need this to display media if there is no text)
  return (
    stringWithoutTags?.trim()?.length > 0 ||
    stringWithHtmlTags.includes("<img") ||
    stringWithHtmlTags.includes("<video") ||
    stringWithHtmlTags.includes("<iframe")
  );
};

export const getTruncatedText = (text: string, length = 20): string => {
  let returnText = text;
  if (text.length > length) {
    returnText = `${text.substring(0, length)}...`;
  }
  return returnText;
};

/**
 *
 * @param str string to convert to sentence case
 * @param split string that separates words in the string
 * @returns the original string, but in sentence case
 */
export function toSentenceCase(str: string, split = " "): string {
  if (typeof str !== "string" || str.trim() === "") {
    return "";
  }

  // Split the string by spaces
  const words = str.split(split);

  // If there is more than one word
  if (words.length > 1) {
    // Convert the first word to sentence case
    words[0] = toCapitalizedWord(words[0]);
    // Convert the remaining words to lowercase
    for (let i = 1; i < words.length; i += 1) {
      words[i] = words[i].toLowerCase();
    }
    // Join the words back into a single string
    return words.join(" ");
  }
  // If it's a single word, dont do anything
  return str;
}

/*
 * Checks if an input string is a valid positive integer
 * Ex: "12px" evaluates to false, "12" evaluates to true
 */
export function isPositiveIntegerString(value: string) {
  return /^\d+$/.test(value);
}

/**
 * Returns a lowercase string with
 * - no special characters
 * - no consecutive spaces or hyphens
 * - trimmed whitespace/hyphens
 * - all spaces replaced by hyphens
 * @param {string} inputSlug The string to turn into a slug
 * @returns A slug
 */
export function stringToSlug(inputSlug?: string): string {
  let returnSlug = "";
  if (inputSlug) {
    returnSlug = inputSlug
      .toLowerCase()
      .replace(/ +/g, "-") // replace consecutive spaces with a single hyphen
      .replace(/-+/g, "-") // replace consecutive hyphens with a single hyphen
      .replace(/^-*|-*$/g, "") // trim hyphens at either end of the string
      .replace(/[^\w-]+/g, ""); // remove special characters

    // maximum slug length is 200 characters
    returnSlug = returnSlug.slice(0, 200);
  }
  return returnSlug;
}

export const preventNonNumericalInput = (e: {
  which: number;
  keyCode: number;
  preventDefault: () => void;
}): void => {
  const charCode = typeof e.which === "undefined" ? e.keyCode : e.which;
  const charStr = String.fromCharCode(charCode);

  if (!charStr.match(/^[0-9]+$/)) e.preventDefault();
};

export const formatPhoneNumber = (phoneNumber?: string | null): string => {
  if (!phoneNumber) return "";

  const cleaned = `${phoneNumber}`.replace(/\D/g, "");

  // North American format: +1 (XXX) XXX-XXXX
  const usMatch = cleaned.match(/^1(\d{3})(\d{3})(\d{4})$/);
  if (usMatch) {
    return `+1 (${usMatch[1]}) ${usMatch[2]}-${usMatch[3]}`;
  }

  // UK format: +44 XXXX XXXXXX
  const ukMatch = cleaned.match(/^44(\d{4})(\d{6})$/);
  if (ukMatch) {
    return `+44 ${ukMatch[1]} ${ukMatch[2]}`;
  }

  // Generic international format: +XX XXX XXX XXXX
  const intMatch = cleaned.match(/^(\d{2})(\d{3})(\d{3})(\d{4})$/);
  if (intMatch) {
    return `+${intMatch[1]} ${intMatch[2]} ${intMatch[3]} ${intMatch[4]}`;
  }

  // Return original if no format matches
  return phoneNumber;
};
